import React from 'react';
import { useLocalStore, useObserver } from 'mobx-react';
import { runInAction } from 'mobx';
import { _COMPARISON_CHART_INIT } from './constants';
import SoccerMatchRelativeRecordModel from './model/record';
import SoccerMatchRankModel from './model/rank';
import { ScoreUtil } from '../../../../../common/utils/scoreUtil';
import { ObjectUtil } from '../../../../../common/utils/objectUtil';
import { ScoreDetailService } from '../../../../../common/services';

const SoccerStatStore = () => {
  const store = useLocalStore(() => ({
    record: {
      recentAwayRecord: [],
      recentHomeRecord: [],
      vsRecord: [],
      selectedRecentRecordTab: 'home',
    },
    teamStat: {
      home: {},
      away: {},
    },
    rank: [],
    loading: true,
    init: false,
    gameId: null,
    get vsRecordSummary() {
      const { vsRecord } = store.record;
      const vsCount = vsRecord
        .slice()
        .map((item) => ScoreUtil.getSummaryScore(item, item.home.id))
        .reduce(
          (a, c) => {
            if (c.home.score > c.away.score) {
              a.win++;
            } else if (c.away.score > c.home.score) {
              a.loss++;
            }

            return a;
          },
          { win: 0, loss: 0 }
        );

      let result = {
        win: { visible: false, text: '' },
      };

      if (vsRecord.length > 0) {
        const winningCountArr = vsRecord.slice().map((item) => ScoreUtil.getSummaryScore(item, item.home.id));
        let winningCount = 0;
        let lossCount = 0;
        let countText = '';

        winningCountArr.some((item) => {
          if (item.home.score > item.away.score) {
            winningCount++;
          }

          return item.away.score > item.home.score;
        });

        winningCountArr.some((item) => {
          if (item.away.score > item.home.score) {
            lossCount++;
          }

          return item.home.score > item.away.score;
        });
      }

      return result;
    },
    get comparisonChart() {
      const { teamStat } = store;
      const { home, away } = teamStat;

      let result = ObjectUtil.deepClone(_COMPARISON_CHART_INIT);

      if (!!home.chart && !!away.chart) {
        Object.keys(_COMPARISON_CHART_INIT).forEach((key) => {
          result[key] = {
            home: home.chart[key],
            away: away.chart[key],
            visible: false,
          };

          if (result[key]['home'].p > 0 && result[key]['away'].p > 0) {
            result[key]['visible'] = true;

            const homeP = result[key]['home'].p;
            const awayP = result[key]['away'].p;

            result[key]['home']['active'] = false;
            result[key]['away']['active'] = false;
            result[key]['visible'] = true;

            if (key === 'pointsLossAverage' || key === 'turnoversAverage') {
              if (homeP > awayP) {
                result[key]['away']['active'] = true;
              } else {
                result[key]['home']['active'] = true;
              }
            } else {
              if (homeP > awayP) {
                result[key]['home']['active'] = true;
              } else {
                result[key]['away']['active'] = true;
              }
            }

            if (homeP === awayP) {
              result[key]['home']['active'] = false;
              result[key]['away']['active'] = false;
            }

            if (result[key]['home'].v === result[key]['away'].v) {
              result[key]['home']['active'] = false;
              result[key]['away']['active'] = false;
            }
          }
        });
      }

      return result;
    },
    async getRecord(gameId) {
      const result = await ScoreDetailService.getRecordBySportsType('soccer', gameId);

      if (!!result) {
        return result;
      }
    },
    async getRank(gameId) {
      const result = await ScoreDetailService.getRankBySportsType('soccer', gameId);

      if (!!result) {
        return result;
      }
    },
    getAll: async (gameId) => {
      store.loading = true;

      const [record, rank] = await Promise.all([store.getRecord(gameId), store.getRank(gameId)]);

      runInAction(() => {
        store.record = new SoccerMatchRelativeRecordModel(record);
        store.rank = new SoccerMatchRankModel(rank);
        store.loading = false;
      });
    },
  }));

  return {
    getAll: store.getAll,
    data: useObserver(() => {
      return {
        init: store.init,
        loading: store.loading,
        record: store.record,
        recentRecordSummary: store.recentRecordSummary,
        teamStat: store.teamStat,
        comparisonChart: store.comparisonChart,
        rank: store.rank,
      };
    }),
  };
};

export default SoccerStatStore;
